import {
  Typography as MuiTypography,
  Divider as MuiDivider,
  Collapse,
  IconButton,
  Box,
} from "@material-ui/core";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { format } from "date-fns";

import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import React, { useMemo, useState } from "react";
import TemperatureHeatmapChartContainer from "../../components/charts/depthHeatmapChart/TemperatureHeatmapChartContainer";
import TemperatureHeatmap15MinChartContainer from "../../components/charts/depthHeatmapChart/TemperatureHeatmap15MinChartContainer";
import RawTable from "../../components/RawTable";
import { parseLocalDate } from "../../utils";

/* ---------- styled helpers ---------- */

const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const ChartSection = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const SubChart = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #e0e0e0;
`;

/* --- default date range (most recent collection season: Apr 1 – Oct 31) --- */
const TODAY = new Date();
export function getSeasonDateRange(date = TODAY) {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed (0 = Jan, 3 = Apr)
  // If before April, show previous year's season
  const seasonYear = month < 3 ? year - 1 : year;
  return {
    start_date: `${seasonYear}-04-02`,
    end_date: `${seasonYear}-11-01`,
  };
}
const { start_date, end_date } = getSeasonDateRange();

/* --- defaults + Zod schema --- */
const DEFAULT_SEARCH_PARAMS = {
  start_date,
  end_date,
};

const searchParamsSchema = z.object({
  start_date: z.string().default(DEFAULT_SEARCH_PARAMS.start_date),
  end_date: z.string().default(DEFAULT_SEARCH_PARAMS.end_date),
});

/* --- endpoints --- */
const DAILY_ENDPOINT = "thermistor-data";
const FIFTEEN_MIN_ENDPOINT = "thermistor-data/15-min";

export default function ContinuousTemperatureProfile() {
  const { params, setParams } = useQueryParams(searchParamsSchema);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDailyTable, setShowDailyTable] = useState(false);
  const [show15MinTable, setShow15MinTable] = useState(false);

  const FILTERS_CONFIG = useMemo(
    () => [
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
    ],
    [params]
  );

  const DAILY_COLUMNS = [
    { title: "Date", field: "rdate", type: "date" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "param_name" },
    { title: "Daily Avg", field: "daily_avg", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    { title: "Depth", field: "depth_value", type: "numeric" },
  ];

  const FIFTEEN_MIN_COLUMNS = [
    { title: "Timestamp", field: "rtimestamp", type: "datetime" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "param_name" },
    { title: "Value", field: "result_value", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    { title: "Depth", field: "depth_value", type: "numeric" },
  ];

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const formattedSelectedDate = selectedDate
    ? format(parseLocalDate(selectedDate), "MM/dd/yyyy")
    : null;

  return (
    <>
      <Helmet title="Continuous Temperature Profile (Sonde)" />

      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Continuous Temperature Profile (Sonde)
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        {/* ---------- About This Page (Consolidated) ---------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page displays <strong>continuous temperature data</strong>{" "}
            collected from thermistor sensors (sondes) deployed at CCR2 (Cherry
            Creek Reservoir Station 2). The sensors record water temperature at
            multiple depths throughout the water column.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Please note:</strong> Data is only collected between{" "}
            <strong>April and October</strong> each year.
          </Typography>
          <Typography variant="body1" gutterBottom>
            The heatmaps show temperature across depth (y-axis) and time
            (x-axis). Colors indicate temperature values, with{" "}
            <strong>cooler blues</strong> for lower temperatures and{" "}
            <strong>warmer reds</strong> for higher temperatures. Gray cells
            indicate missing data.
          </Typography>
          <Typography variant="body1">
            <strong>Tip:</strong> Click on any cell in the daily chart to view
            detailed 15-minute interval data for that date.
          </Typography>
        </Section>

        {/* ---------- Filters ---------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS_CONFIG} onChange={setParams} />
        </Section>

        {/* ---------- Charts Section (Both charts together) ---------- */}
        <ChartSection>
          {/* Daily Average Chart */}
          <TemperatureHeatmapChartContainer
            start_date={params.start_date}
            end_date={params.end_date}
            height={400}
            title="Daily Average Temperature at CCR2"
            onDateSelect={handleDateSelect}
          />

          {/* 15-Minute Chart (appears inline below daily) */}
          {selectedDate && (
            <SubChart>
              <TemperatureHeatmap15MinChartContainer
                selectedDate={selectedDate}
                height={400}
                title={`15-Minute Temperature Data for ${formattedSelectedDate}`}
              />
            </SubChart>
          )}
        </ChartSection>

        {/* ---------- Raw Data Tables (Collapsible) ---------- */}
        <Section>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ cursor: "pointer" }}
            onClick={() => setShowDailyTable(!showDailyTable)}
          >
            <Typography variant="h5" component="h2">
              Daily Average Data Table
            </Typography>
            <IconButton size="small">
              {showDailyTable ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={showDailyTable}>
            <Box mt={2}>
              <RawTable
                endPoint={DAILY_ENDPOINT}
                params={params}
                columns={DAILY_COLUMNS}
              />
            </Box>
          </Collapse>
        </Section>

        {selectedDate && (
          <Section>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ cursor: "pointer" }}
              onClick={() => setShow15MinTable(!show15MinTable)}
            >
              <Typography variant="h5" component="h2">
                15-Minute Data Table for {formattedSelectedDate}
              </Typography>
              <IconButton size="small">
                {show15MinTable ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={show15MinTable}>
              <Box mt={2}>
                <RawTable
                  endPoint={FIFTEEN_MIN_ENDPOINT}
                  params={{ rdate: selectedDate }}
                  columns={FIFTEEN_MIN_COLUMNS}
                />
              </Box>
            </Collapse>
          </Section>
        )}
      </main>
    </>
  );
}
